/**
 * Checklist Record create result classification.
 * Reuses postRequest __httpStatus via getServiceTaskHttpStatus. Does not reuse ServiceTask message keys.
 * HTTP 4xx/5xx is never treated as offline. There is no offline queue or automatic retry.
 */
import { firstValidationDetail, getServiceTaskHttpStatus } from './serviceTaskCreateHttp';

export const CHECKLIST_RECORD_CREATE_MESSAGES = Object.freeze({
  success: 'ChecklistRecord.Create.RecordSaved',
  validation: 'ChecklistRecord.Create.ValidationFailed',
  permission: 'ChecklistRecord.Create.PermissionDenied',
  notFound: 'ChecklistRecord.Create.NotFound',
  server: 'ChecklistRecord.Create.ActionFailed',
  network: 'ChecklistRecord.Create.NotSaved',
  offline: 'ChecklistRecord.Create.OfflineNotSaved',
});

export function canCreateChecklistRecord(userOrPermissions) {
  const permissions = Array.isArray(userOrPermissions)
    ? userOrPermissions
    : (userOrPermissions?.permissions ?? []);
  return permissions.includes('add checklists') || permissions.includes('add maintenance');
}

function hasExpectedCreateData(result) {
  return result?.data != null && typeof result.data === 'object' && !Array.isArray(result.data);
}

export function interpretChecklistRecordCreateResponse(result) {
  if (result == null || typeof result !== 'object' || Array.isArray(result)) {
    return {
      ok: false,
      kind: 'invalid',
      messageKey: CHECKLIST_RECORD_CREATE_MESSAGES.server,
      detail: null,
    };
  }

  const httpStatus = getServiceTaskHttpStatus(result);
  const bodyStatus = typeof result.status === 'number' ? result.status : null;
  const hasErrors = Boolean(result.errors && typeof result.errors === 'object' && !Array.isArray(result.errors));
  const successHttp = typeof httpStatus === 'number' && httpStatus >= 200 && httpStatus < 300;

  if (successHttp && bodyStatus === 200 && hasExpectedCreateData(result)) {
    return {
      ok: true,
      kind: 'success',
      httpStatus,
      messageKey: CHECKLIST_RECORD_CREATE_MESSAGES.success,
    };
  }

  const status = httpStatus ?? bodyStatus;
  if (status === 422 || hasErrors) {
    return {
      ok: false,
      kind: 'validation',
      httpStatus: status ?? 422,
      messageKey: CHECKLIST_RECORD_CREATE_MESSAGES.validation,
      detail: firstValidationDetail(result.errors, null),
      fieldErrors: hasErrors ? result.errors : {},
    };
  }
  if (status === 403) {
    return { ok: false, kind: 'permission', httpStatus: 403, messageKey: CHECKLIST_RECORD_CREATE_MESSAGES.permission };
  }
  if (status === 404) {
    return { ok: false, kind: 'not_found', httpStatus: 404, messageKey: CHECKLIST_RECORD_CREATE_MESSAGES.notFound };
  }
  if (status === 401 || status === 419) {
    return { ok: false, kind: 'auth', httpStatus: status, messageKey: CHECKLIST_RECORD_CREATE_MESSAGES.server };
  }
  if (status === 429 || (status != null && status >= 500)) {
    return { ok: false, kind: 'server', httpStatus: status, messageKey: CHECKLIST_RECORD_CREATE_MESSAGES.server };
  }
  return { ok: false, kind: 'server', httpStatus: status, messageKey: CHECKLIST_RECORD_CREATE_MESSAGES.server };
}

export function interpretChecklistRecordCreateTransportError(isOnline) {
  if (isOnline === false) {
    return { ok: false, kind: 'offline', messageKey: CHECKLIST_RECORD_CREATE_MESSAGES.offline };
  }
  return { ok: false, kind: 'network', messageKey: CHECKLIST_RECORD_CREATE_MESSAGES.network };
}
